import { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Image,
  LoadingOverlay,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconSearch, IconUserPlus, IconRefresh } from "@tabler/icons-react";
import sadrobot from "@/assets/sadrobot.png";
import { useAuthStore } from "@utils/stores/authStore";
import { ROLE_INFO } from "@/constants";
import { notifications } from "@mantine/notifications";
import { changeRole } from "@/utils/api";
import { addMemberToBusiness } from "@/utils/api";
import { deletePendingMembers } from "@/utils/api/members";
import InviteTeamModal from "@/pages/Settings/InviteTeamModal/InviteTeamModal";
import { SetCreditsModal } from "@pages/Credits/components/SetCreditsModal";
import EditAccessTypeModal from "@pages/Credits/components/EditAccessTypeModal";
import ChangeRoleModal from "@pages/Credits/components/ChangeRoleModal";
import { useUnifiedTeamCredits } from "./hooks/useUnifiedTeamCredits";
import { useCreditOperations } from "./hooks/useCreditOperations";
import { reloadExtensionOnboardingSteps } from "@utils/extensionClient";
import { TeamMemberRow } from "./components/TeamMemberRow";
import { RequestCreditsSection } from "./components/RequestCreditsSection";
import type { TeamManagerProps, EnrichedTeamMember } from "./types";
import "./TeamManager.scss";

/**
 * Team Manager Component
 *
 * Official team member management component with credit allocation capabilities.
 *
 * Features:
 * - Team member list grouped by role
 * - Inline credit actions (Set Credits, Edit Access)
 * - Team member actions (Archive, Delete pending)
 * - Credit request section (for team members)
 * - Search and filter (show archived)
 */
export const TeamManager = ({ onMembersUpdated }: TeamManagerProps) => {
  const { role, isOwner } = useAuthStore();
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Modals state
  const [openedInviteTeamModal, setOpenedInviteTeamModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [lockRole, setLockRole] = useState(false);
  const [openedSetCreditsModal, setOpenedSetCreditsModal] = useState(false);
  const [openedEditAccessModal, setOpenedEditAccessModal] = useState(false);
  const [openedChangeRoleModal, setOpenedChangeRoleModal] = useState(false);
  const [selectedMember, setSelectedMember] =
    useState<EnrichedTeamMember | null>(null);

  // Fetch unified data
  const {
    businessBalance,
    userAllocatedCredits,
    hasCreditRequest,
    signupSource,
    members,
    membersCount,
    isLoading,
    membersError,
    refetchBalance,
  } = useUnifiedTeamCredits(refreshTrigger);

  // Credit operations
  const {
    handleSetCredits,
    handleRequestCredits,
    isRequestingCredits,
    isAllocating,
  } = useCreditOperations();

  // Filter members
  const filteredMembers = useMemo(() => {
    if (!members || !Array.isArray(members)) {
      return [];
    }

    return members.filter((member) => {
      if (!member) return false;

      // Filter by archived status
      const statusMatch = showArchived
        ? member.status === "inactive"
        : member.status === "active" || member.status === "pending";

      // Filter by search query
      const searchMatch =
        !searchQuery ||
        member.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchQuery.toLowerCase());

      return statusMatch && searchMatch;
    });
  }, [members, showArchived, searchQuery]);

  // Group members by role
  const groupedMembers = useMemo(() => {
    const groups: { [key: string]: EnrichedTeamMember[] } = {
      owner: [],
      team_lead: [],
      team_member: [],
    };

    filteredMembers.forEach((member) => {
      const memberRole = member.role?.toLowerCase();
      if (memberRole in groups) {
        groups[memberRole].push(member);
      }
    });

    return groups;
  }, [filteredMembers]);

  const remainingSeats = membersCount?.teamCapacity?.remaining ?? 0;

  // Handlers
  const handleOpenInviteModal = (
    roleValue?: string,
    shouldLockRole = false
  ) => {
    setSelectedRole(roleValue || null);
    setLockRole(shouldLockRole);
    setOpenedInviteTeamModal(true);
  };

  const handleInviteMembers = async (
    users: { email: string; role: string; autoAllocateCredits?: number }[]
  ) => {
    let hasSuccessfulInvites = false;

    for (const user of users) {
      try {
        const requestBody: any = {
          email: [user.email],
          role: user.role,
        };

        if (user.autoAllocateCredits && user.autoAllocateCredits > 0) {
          requestBody.autoAllocateCredits = user.autoAllocateCredits;
        }

        const response = await addMemberToBusiness(requestBody);
        const successfulEmails =
          response?.data?.content?.successfulEmails ?? [];
        const failedEmails = response?.data?.content?.failedEmails ?? [];

        if (successfulEmails.length > 0) {
          hasSuccessfulInvites = true;
          notifications.show({
            color: "green",
            title: "Success",
            message: `Successfully invited: ${successfulEmails.join(", ")}`,
          });
          setRefreshTrigger((prev) => !prev);
          onMembersUpdated?.();
        }

        if (failedEmails.length > 0) {
          notifications.show({
            color: "red",
            title: "Error",
            message: `Failed to invite: ${failedEmails.join(", ")}`,
          });
        }
      } catch (error) {
        console.error(error);
        notifications.show({
          color: "red",
          title: "Error",
          message: "Something went wrong. Please try again later.",
        });
      }
    }

    // If any invites were successful, notify extension to reload onboarding steps
    if (hasSuccessfulInvites) {
      try {
        await reloadExtensionOnboardingSteps();
      } catch (error) {
        console.error("Failed to reload extension onboarding steps:", error);
      }
    }
  };

  const handleArchive = async (userId: string, archived: boolean) => {
    try {
      await changeRole({ userId, archive: archived, role });
      setRefreshTrigger((prev) => !prev);
      notifications.show({
        title: "Success",
        message: `Member archive status changed successfully`,
        color: "green",
      });
      onMembersUpdated?.();
    } catch (error) {
      console.error("Error archiving member:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update member status. Please try again.",
        color: "red",
      });
    }
  };

  const handleDeletePending = async (userId: string) => {
    const member = members.find((m) => m.userId === userId);
    if (!member) return;

    try {
      await deletePendingMembers({ email: member.email });
      setRefreshTrigger((prev) => !prev);
      notifications.show({
        title: "Success",
        message: `Successfully deleted pending invite for ${member.email}`,
        color: "green",
      });
      onMembersUpdated?.();
    } catch (error) {
      console.error("Error deleting pending member:", error);
      notifications.show({
        title: "Error",
        message:
          "Failed to delete pending member invite. Please try again later.",
        color: "red",
      });
    }
  };

  const handleOpenSetCreditsModal = (userId: string) => {
    const member = members.find((m) => m.userId === userId);
    if (member) {
      setSelectedMember(member);
      setOpenedSetCreditsModal(true);
    }
  };

  const handleOpenEditAccessModal = (userId: string) => {
    const member = members.find((m) => m.userId === userId);
    if (member) {
      setSelectedMember(member);
      setOpenedEditAccessModal(true);
    }
  };

  const handleOpenChangeRoleModal = (userId: string) => {
    const member = members.find((m) => m.userId === userId);
    if (member) {
      setSelectedMember(member);
      setOpenedChangeRoleModal(true);
    }
  };

  const handleSetCreditsSubmit = (userId: string, amount: number) => {
    // Note: amount is already the difference calculated by SetCreditsModal
    handleSetCredits(userId, amount, () => {
      // Only close modal and refresh after successful API response
      setOpenedSetCreditsModal(false);
      setRefreshTrigger((prev) => !prev);
      onMembersUpdated?.();
    });
  };

  const getRoleBadgeColor = (roleKey: string) => {
    if (roleKey === "owner") return "grape";
    if (roleKey === "team_lead") return "blue";
    return "green";
  };

  // Show error state if there's an error loading members
  if (membersError && !isLoading) {
    return (
      <Box pos="relative" className="unified-team-credits-manager">
        <Center py="xl">
          <Stack align="center" gap="md" maw={500}>
            <Box w={150}>
              <Image src={sadrobot} alt="Error loading data" />
            </Box>
            <Title order={3} ta="center">
              Oops! Something went wrong
            </Title>
            <Text ta="center" c="dimmed">
              We couldn't load your team members. Please try again.
            </Text>
            <Button
              leftSection={<IconRefresh size={18} />}
              onClick={() => setRefreshTrigger((prev) => !prev)}
              variant="light"
            >
              Retry
            </Button>
          </Stack>
        </Center>
      </Box>
    );
  }

  return (
    <Box pos="relative" className="team-manager">
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 1, opacity: 0.75 }}
      />

      <Stack gap="lg">
        {/* Team Members Section */}
        {/* Actions Bar */}
        <Group
          justify="space-between"
          pb="md"
          style={{ borderBottom: "1px solid #e9ecef" }}
        >
          <TextInput
            placeholder="Search for a teammate"
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            style={{ width: 280 }}
          />
          <Switch
            label="Show archived"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.currentTarget.checked)}
            labelPosition="left"
            size="sm"
          />
        </Group>

        {/* Members Grouped by Role */}
        <Stack gap="md">
          {Object.entries(ROLE_INFO).map(([roleKey, roleInfo]) => {
            const membersInRole = groupedMembers[roleKey] || [];

            return (
              <Box key={roleKey} className="role-section">
                <Group justify="space-between" className="role-section-header">
                  <Group gap="xs">
                    <Text
                      c="dimmed"
                      className="role-section-title"
                      style={{ color: roleInfo.color }}
                    >
                      {roleInfo.title}
                    </Text>
                    <Badge
                      variant="light"
                      color={getRoleBadgeColor(roleKey)}
                      radius="xl"
                      size="sm"
                    >
                      {membersInRole.length}
                    </Badge>
                  </Group>
                </Group>

                {/* Role description */}
                <Text size="sm" c="dimmed" mb="md">
                  {roleInfo.description}
                </Text>

                {/* Grid layout with cards */}
                <Box className="team-members-grid">
                  {/* Member cards first */}
                  {membersInRole.map((member) => {
                    const roleCardClass =
                      roleKey === "owner"
                        ? "owner-card"
                        : roleKey === "team_lead"
                          ? "team-lead-card"
                          : "team-member-card-bg";

                    const requestedClass = member.creditRequest
                      ? "credit-requested"
                      : "";

                    return (
                      <Box
                        key={member.userId}
                        className={`team-member-card ${roleCardClass} ${requestedClass}`}
                        onClick={() => {
                          if (member.creditRequest && isOwner()) {
                            handleOpenSetCreditsModal(member.userId);
                          }
                        }}
                        style={{
                          cursor:
                            member.creditRequest && isOwner()
                              ? "pointer"
                              : "default",
                        }}
                      >
                        <TeamMemberRow
                          member={member}
                          onSetCredits={handleOpenSetCreditsModal}
                          onEditAccessType={handleOpenEditAccessModal}
                          onChangeRole={handleOpenChangeRoleModal}
                          onArchive={handleArchive}
                          onDelete={handleDeletePending}
                          showCreditActions={isOwner()}
                        />
                      </Box>
                    );
                  })}

                  {/* Show add card AFTER members for non-owner roles */}
                  {roleKey !== "owner" && (
                    <Box
                      className="add-member-card"
                      onClick={() =>
                        remainingSeats > 0 &&
                        handleOpenInviteModal(
                          roleKey === "team_lead" ? "Team Lead" : "Team Member",
                          true
                        )
                      }
                      style={{
                        opacity: remainingSeats === 0 ? 0.5 : 1,
                        cursor:
                          remainingSeats === 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      <IconUserPlus
                        className="add-icon"
                        size={48}
                        stroke={1.5}
                      />
                      <Text className="add-text">Add {roleInfo.title}</Text>
                    </Box>
                  )}

                  {/* Show empty state if no members and no add card (owner section) */}
                  {membersInRole.length === 0 && roleKey === "owner" && (
                    <Box className="empty-state">
                      <Text size="sm" c="dimmed" fw={500} fs="italic">
                        No {roleInfo.title.toLowerCase()}s yet
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}

          {filteredMembers.length === 0 && (
            <Paper withBorder p="xl" radius="md">
              <Text size="sm" c="dimmed" ta="center">
                {searchQuery
                  ? "No members found matching your search"
                  : showArchived
                    ? "No archived members"
                    : "No active members"}
              </Text>
            </Paper>
          )}
        </Stack>

        {/* Request Credits Section (Team Members Only) */}
        {!isOwner() && role !== "client" && (
          <RequestCreditsSection
            allocatedCredits={userAllocatedCredits}
            hasRequestedCredits={hasCreditRequest}
            onRequestCredits={handleRequestCredits}
            isRequesting={isRequestingCredits}
          />
        )}
      </Stack>

      {/* Modals */}
      <InviteTeamModal
        opened={openedInviteTeamModal}
        setOpened={setOpenedInviteTeamModal}
        onInvite={handleInviteMembers}
        remainingSeats={remainingSeats}
        membersList={members as any}
        defaultRole={selectedRole || undefined}
        lockRole={lockRole}
      />

      {selectedMember && (
        <>
          <SetCreditsModal
            opened={openedSetCreditsModal}
            onClose={() => setOpenedSetCreditsModal(false)}
            teamMember={{
              userId: selectedMember.userId,
              email: selectedMember.email,
              role: selectedMember.role,
              firstName: selectedMember.firstName,
              lastName: selectedMember.lastName,
              status: selectedMember.status,
              allocatedCredits: selectedMember.allocatedCredits,
              businessUse: selectedMember.businessUse,
            }}
            businessBalance={businessBalance ?? 0}
            onSetCredits={handleSetCreditsSubmit}
            isLoading={isAllocating}
          />

          <EditAccessTypeModal
            opened={openedEditAccessModal}
            onClose={() => {
              setOpenedEditAccessModal(false);
              setRefreshTrigger((prev) => !prev);
              refetchBalance(); // Refetch balance data to update businessUse badge
              onMembersUpdated?.();
            }}
            teamMember={{
              userId: selectedMember.userId,
              email: selectedMember.email,
              role: selectedMember.role,
              firstName: selectedMember.firstName,
              lastName: selectedMember.lastName,
              status: selectedMember.status,
              businessUse: selectedMember.businessUse,
              allocatedCredits: selectedMember.allocatedCredits,
            }}
            signupSource={signupSource}
          />

          <ChangeRoleModal
            opened={openedChangeRoleModal}
            onClose={() => setOpenedChangeRoleModal(false)}
            teamMember={{
              userId: selectedMember.userId,
              email: selectedMember.email,
              role: selectedMember.role,
              firstName: selectedMember.firstName,
              lastName: selectedMember.lastName,
              status: selectedMember.status,
            }}
            onSuccess={() => {
              setRefreshTrigger((prev) => !prev);
              onMembersUpdated?.();
            }}
          />
        </>
      )}
    </Box>
  );
};
